import React, { useEffect, useState } from 'react';
import { Stack, Typography, Button, Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useOutletContext } from 'react-router-dom';

import ReportsBackIcon from '../../../../assets/icons/reports-back-icon';
import { ReportTypes } from '../../../../types';
import { FieldSafetyReportsDiagram } from '../../../../components/field-safety-reports-diagram';
import { FieldSafetyReportsList } from '../../../../components/lists/field-safety-reports-list';
import { FieldSafetyReportDetail } from '../../../../components/field-safety-report-detail';

interface ContextType {
    setReportTitle: (title: string) => void;
    setReportDescText: (desc: string) => void;
}

export const FieldSafetyReportsListScreen: React.FC = () => {
    const theme = useTheme();
    const accent = theme.palette.primary.main;
    const { setReportTitle, setReportDescText } = useOutletContext<ContextType>();
    const navigate = useNavigate();
    const [selectedReport, setSelectedReport] = useState<ReportTypes.Model | null>(null);

    useEffect(() => {
        setReportTitle('Field Safety Reports');
        setReportDescText(
            'Review reports filed by caregivers in the field. Reports broadcast a warning to teammates who enter the same area on mobile.'
        );
    }, [setReportTitle, setReportDescText]);

    if (selectedReport) {
        return (
            <FieldSafetyReportDetail
                report={selectedReport}
                onBack={() => setSelectedReport(null)}
            />
        );
    }

    return (
        <Stack>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 56,
                    marginTop: 4,
                    marginBottom: 3,
                }}
            >
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => navigate('/reports')}
                    sx={{
                        border: 0,
                        padding: 1,
                        boxShadow:
                            '0 0 2px 0 rgba(145, 158, 171, 0.24),0 16px 32px -4px rgba(145, 158, 171, 0.24)',
                    }}
                >
                    <ReportsBackIcon />
                    <Typography fontWeight={400} fontSize={14} color={'black'} marginLeft={1}>
                        Back
                    </Typography>
                </Button>
                <Box
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        display: { xs: 'none', md: 'block' },
                        pointerEvents: 'none',
                        maxWidth: 640,
                        width: '100%',
                        px: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            letterSpacing: '0.16em',
                            color: alpha(accent, 0.9),
                            textTransform: 'uppercase',
                            lineHeight: 1.4,
                            mb: 0.5,
                        }}
                    >
                        How it works
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 15,
                            fontWeight: 500,
                            color: 'text.primary',
                            lineHeight: 1.45,
                        }}
                    >
                        A caregiver flags a hazard in the field. The next teammate to enter the area gets a heads up.
                    </Typography>
                </Box>
            </Box>
            <FieldSafetyReportsDiagram />
            <FieldSafetyReportsList onViewReport={setSelectedReport} />
        </Stack>
    );
};

export default FieldSafetyReportsListScreen;
